import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PageHero from "../shared/PageHero"
import Section from "../shared/Section"
import SectionTitle from "../shared/SectionTitle"
import CaseStudyCard from "../shared/CaseStudyCard"
import CtaBanner from "../shared/CtaBanner"
import TvButton from "../shared/TvButton"
import TvYoutube from "../shared/TvYoutube"
import ProjectCard from "../shared/ProjectCard"
import { useLocation } from "react-router-dom"
import { COLORS } from "../../constants"
import resumeData from "../../utils/resumeData"
import { caseStudies } from "../../utils/siteContent"
import usePageMeta from "../../utils/usePageMeta"

const featured = caseStudies.filter((c) => c.featured)
const more = caseStudies.filter((c) => !c.featured)

function Work() {
    const { hash } = useLocation()
    usePageMeta({
        title: 'Work & Case Studies',
        description: 'Software engineering case studies: application modernization and TypeScript migration, AWS Cognito authentication, DynamoDB cost optimization, production React and AWS applications, and automated testing. Plus live client websites.',
        path: '/work',
    })

    return (
        <>
            <PageHero
                eyebrow="Work & case studies"
                title="Selected work"
                intro="Case studies from my full-time engineering roles, plus live websites and apps I’ve built on my own. The common thread: find what’s making a system hard to work with, then fix it in a way the next person can maintain."
                actions={
                    <>
                        <TvButton text="Start a Project" to="/contact" icon={<ArrowForwardIcon />} />
                        <TvButton text="See a live client website" link="#websites" variant="outline" tone="dark" />
                    </>
                }
            />

            <Section tone="light">
                <SectionTitle
                    eyebrow="Professional experience"
                    text="Case studies from my engineering roles"
                    intro="These come from my full-time employment, not freelance clients. Most were team efforts; where I led or implemented the work myself, the card says so. Details are kept general to respect employer and customer confidentiality."
                />
                <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    {featured.map((c, i) => (
                        <Box key={c.id} sx={{ gridColumn: i === 0 ? '1 / -1' : undefined, display: 'grid' }}>
                            <CaseStudyCard study={c} wide={i === 0} />
                        </Box>
                    ))}
                </Box>

                <Accordion
                    disableGutters
                    elevation={0}
                    // Open automatically when a link points at one of the cards inside (e.g. /work#real-time-updates)
                    defaultExpanded={more.some((c) => `#${c.id}` === hash)}
                    sx={{ mt: 3, border: `1px solid ${COLORS.border}`, borderRadius: '12px !important', '&:before': { display: 'none' } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
                        <Box>
                            <Typography variant="h4" component="h3">More professional work ({more.length})</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                {more.map((c) => c.title).join(' · ')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                        {more.map((c) => <CaseStudyCard key={c.id} study={c} />)}
                    </AccordionDetails>
                </Accordion>
            </Section>

            <Section id="websites">
                <SectionTitle
                    eyebrow="Freelance & side projects"
                    text="Live projects you can try"
                    intro="Websites and apps I’ve built outside my day job, for a client and for myself, that are out in the world right now."
                />
                <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    {resumeData.projects.map((project) => <ProjectCard key={project.name} project={project} />)}
                </Box>
            </Section>

            <Section tone="light" tight>
                <Accordion
                    disableGutters
                    elevation={0}
                    TransitionProps={{ unmountOnExit: true }}
                    sx={{ border: `1px solid ${COLORS.border}`, borderRadius: '12px !important', '&:before': { display: 'none' } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
                        <Box>
                            <Typography variant="h4" component="h2">Early work</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                My 2018 bootcamp projects, kept for anyone curious where I started.
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3, display: 'grid', gap: 4 }}>
                        {resumeData.earlyProjects.map((project) => (
                            <Box key={project.name}>
                                <Typography variant="h5" component="h3">{project.name}</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, mb: 2, color: 'text.secondary' }}>{project.description}</Typography>
                                <TvYoutube videoId={project.link} />
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            </Section>

            <CtaBanner />
        </>
    )
}

export default Work
